import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { ConfigService } from 'src/app/_services/config.service';

@Component({
	standalone: false,
	templateUrl: '503.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [
		`
			:host {
				display: block;
			}

			.server-down-container {
				min-height: 100vh;
				display: flex;
				align-items: center;
				justify-content: center;
				background: var(--kcs-background, linear-gradient(147.38deg, #06b6d4 0%, #155e75 100%));
				padding: 24px;
			}

			.error-content {
				position: relative;
				background: var(--kcs-surface-card, #ffffff);
				border: 1px solid var(--kcs-surface-border, #e2e8f0);
				border-radius: 1rem;
				padding: 48px 44px 40px;
				max-width: 560px;
				width: 100%;
				text-align: center;
				box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
				animation: slideUp 0.5s ease-out;
				overflow: hidden;
			}

			/* Striscia colore brand in cima alla card */
			.error-content::before {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				height: 5px;
				background: var(--kcs-primary, #408ee2);
			}

			@keyframes slideUp {
				from {
					opacity: 0;
					transform: translateY(24px);
				}
				to {
					opacity: 1;
					transform: translateY(0);
				}
			}

			.brand-logo {
				max-height: 60px;
				max-width: 220px;
				object-fit: contain;
				margin-bottom: 28px;
			}

			.error-icon {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 80px;
				height: 80px;
				border-radius: 50%;
				background: color-mix(in srgb, var(--kcs-primary, #408ee2) 12%, transparent);
				color: var(--kcs-primary, #408ee2);
				margin-bottom: 24px;
			}

			.error-icon i {
				font-size: 34px;
				animation: pulse 2.2s ease-in-out infinite;
			}

			@keyframes pulse {
				0%,
				100% {
					opacity: 1;
				}
				50% {
					opacity: 0.55;
				}
			}

			.error-title {
				font-size: 26px;
				font-weight: 700;
				color: var(--kcs-text, #1e293b);
				margin-bottom: 12px;
				line-height: 1.25;
			}

			.error-message {
				font-size: 15px;
				color: var(--kcs-secondary-text, #64748b);
				margin-bottom: 28px;
				line-height: 1.6;
			}

			.error-details {
				background: var(--kcs-surface-ground, #f1f5f9);
				border: 1px solid var(--kcs-surface-border, #e2e8f0);
				border-radius: 0.75rem;
				padding: 16px 20px;
				margin-bottom: 28px;
				text-align: left;
			}

			.detail-item {
				display: flex;
				justify-content: space-between;
				gap: 12px;
				font-size: 13px;
				color: var(--kcs-secondary-text, #64748b);
				padding: 4px 0;
			}

			.detail-item strong {
				color: var(--kcs-text, #1e293b);
				font-weight: 600;
			}

			.status-badge {
				display: inline-flex;
				align-items: center;
				gap: 6px;
				font-weight: 600;
				color: var(--kcs-danger, #dc3545);
			}

			.status-badge .dot {
				width: 8px;
				height: 8px;
				border-radius: 50%;
				background: var(--kcs-danger, #dc3545);
				animation: pulse 1.6s ease-in-out infinite;
			}

			.action-buttons {
				display: flex;
				gap: 12px;
				justify-content: center;
				margin-bottom: 24px;
				flex-wrap: wrap;
			}

			.kcs-btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
				padding: 11px 22px;
				border-radius: 0.5rem;
				font-weight: 600;
				font-size: 14px;
				border: 1px solid transparent;
				cursor: pointer;
				transition: all var(--transition-duration, 0.2s) ease;
				min-width: 150px;
			}

			.kcs-btn-primary {
				background: var(--kcs-primary, #408ee2);
				color: var(--kcs-primary-text, #fff);
			}

			.kcs-btn-primary:hover:not(:disabled) {
				background: var(--kcs-primary-hover, #007bff);
				color: var(--kcs-primary-hover-text, #fff);
				transform: translateY(-2px);
			}

			.kcs-btn:disabled {
				opacity: 0.6;
				cursor: not-allowed;
				transform: none;
			}

			.kcs-btn-secondary {
				background: transparent;
				color: var(--kcs-secondary-text, #64748b);
				border-color: var(--kcs-surface-border, #e2e8f0);
			}

			.kcs-btn-secondary:hover {
				background: var(--kcs-surface-ground, #f1f5f9);
				transform: translateY(-2px);
			}

			.loading {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
			}

			.spinner {
				width: 15px;
				height: 15px;
				border: 2px solid transparent;
				border-top: 2px solid currentColor;
				border-radius: 50%;
				animation: spin 1s linear infinite;
			}

			@keyframes spin {
				to {
					transform: rotate(360deg);
				}
			}

			.auto-retry {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-wrap: wrap;
				gap: 8px 14px;
				font-size: 13px;
				color: var(--kcs-secondary-text, #64748b);
				margin-bottom: 24px;
			}

			.auto-retry-info {
				display: inline-flex;
				align-items: center;
				gap: 7px;
			}

			.auto-retry-info i {
				color: var(--kcs-primary, #408ee2);
			}

			.disable-auto-retry {
				background: none;
				border: none;
				color: var(--kcs-primary, #408ee2);
				font-size: 12px;
				cursor: pointer;
				text-decoration: underline;
				padding: 0;
			}

			.contact-info {
				padding-top: 20px;
				border-top: 1px solid var(--kcs-surface-border, #e2e8f0);
				color: var(--kcs-secondary-text, #64748b);
				font-size: 13px;
			}

			.contact-info a {
				color: var(--kcs-primary, #408ee2);
				text-decoration: none;
				font-weight: 600;
			}

			.contact-info a:hover {
				text-decoration: underline;
			}

			@media (max-width: 576px) {
				.error-content {
					padding: 36px 24px 28px;
				}

				.error-title {
					font-size: 22px;
				}

				.action-buttons {
					flex-direction: column;
					align-items: stretch;
				}

				.kcs-btn {
					width: 100%;
				}
			}
		`,
	],
})
export class P503Component implements OnInit, OnDestroy {
	lastChecked: Date = new Date();
	isRetrying: boolean = false;
	nextRetryIn: number = 0;
	autoRetryEnabled: boolean = true;
	autoRetryCountdown: number = 30;

	companyLogo: string | null = null;
	companyName: string = '';

	private retrySubscription?: Subscription;
	private autoRetrySubscription?: Subscription;
	private countdownSubscription?: Subscription;

	constructor(
		private http: HttpClient,
		private router: Router,
		private configService: ConfigService,
	) {}

	ngOnInit() {
		this.applyCompanyBranding();
		this.startAutoRetry();
	}

	/**
	 * Applica tema + logo + nome dell'azienda corrente.
	 * Necessario perché questa pagina può essere mostrata anche prima che l'AuthGuard
	 * imposti il tema (es. fallimento del primo caricamento all'avvio dell'app).
	 */
	private applyCompanyBranding() {
		const tema = localStorage.getItem('theme') || 'default';
		document.documentElement.setAttribute('kcs-theme', tema);

		const logo = this.configService.getCompanyLogo();
		this.companyLogo = logo && logo.startsWith('data:') ? logo : null;
		this.companyName = localStorage.getItem('def_company_name') || '';
	}

	ngOnDestroy() {
		this.clearSubscriptions();
	}

	retryConnection() {
		if (this.isRetrying) return;

		this.isRetrying = true;
		this.lastChecked = new Date();

		const url = this.configService.serviceUrl_Login + 'translate/' + this.configService.language;
		console.log(url);
		this.http
			.get(url, {
				observe: 'response',
				responseType: 'text',
			})
			.subscribe({
				next: (response: any) => {
					if (response.status === 200) {
						window.location.href = '/';
					}
					this.isRetrying = false;
				},
				error: (error: any) => {
					this.isRetrying = false;
					console.log('Server still down:', error);
				},
			});

		// Start countdown for next retry
		this.nextRetryIn = 10;
		this.countdownSubscription = interval(1000).subscribe(() => {
			this.nextRetryIn--;
			if (this.nextRetryIn <= 0) {
				this.countdownSubscription?.unsubscribe();
			}
		});
	}

	refreshPage() {
		window.location.reload();
	}

	startAutoRetry() {
		if (!this.autoRetryEnabled) return;

		this.autoRetryCountdown = 30;

		const countdown = interval(1000).subscribe(() => {
			this.autoRetryCountdown--;
			if (this.autoRetryCountdown <= 0) {
				countdown.unsubscribe();
				this.retryConnection();
				// Restart auto-retry cycle
				setTimeout(() => this.startAutoRetry(), 15000);
			}
		});

		this.autoRetrySubscription = countdown;
	}

	disableAutoRetry() {
		this.autoRetryEnabled = false;
		this.autoRetrySubscription?.unsubscribe();
	}

	private clearSubscriptions() {
		this.retrySubscription?.unsubscribe();
		this.autoRetrySubscription?.unsubscribe();
		this.countdownSubscription?.unsubscribe();
	}
}
